import { IAPIRoutes } from "../../../../config";

export type CarePlanDocumentJson = Record<string, any>;

export interface ICompassusCarePlan {
  id: number | null;
  patient_id: number;
  document_json: CarePlanDocumentJson;
  document_html: string;
  plain_text: string;
  preview_text: string;
  revision: number;
  last_saved_at: string | null;
  last_saved_by_user_id: number | null;
  last_saved_by_name: string | null;
  last_saved_by_role_title: string | null;
  last_version_id: number | null;
  last_version_created_at: string | null;
  has_unsnapshotted_changes: boolean;
  pending_contributors_json: Array<{
    user_id: number | null;
    name: string | null;
    role_title: string | null;
  }> | null;
  editing_user_id: number | null;
  editing_user_name: string | null;
  editing_user_role_title: string | null;
  editing_session_id: string | null;
  editing_started_at: string | null;
  editing_heartbeat_at: string | null;
  editing_expires_at: string | null;
  is_edit_locked: boolean;
  exists: boolean;
}

export interface ICompassusCarePlanVersion {
  id: number;
  care_plan_id: number;
  patient_id: number;
  clinic_id: number;
  version_number: number;
  version_name: string | null;
  document_json: CarePlanDocumentJson;
  document_html: string;
  plain_text: string;
  preview_text: string;
  revision: number;
  contributors_json: Array<{
    user_id: number | null;
    name: string | null;
    role_title: string | null;
  }> | null;
  created_reason: "initial" | "timed_snapshot" | "before_restore";
  created_at: string;
}

export interface ICompassusCarePlanDiffLine {
  kind: "context" | "added" | "removed";
  text: string;
}

export interface IPatchCompassusCarePlanRequest {
  base_revision: number;
  editing_session_id?: string | null;
  document_json: CarePlanDocumentJson;
  document_html: string;
  plain_text: string;
}

export class CompassusCarePlanApiError extends Error {
  code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = "CompassusCarePlanApiError";
    this.code = code;
  }
}

function carePlanApiError(response: any, fallback: string) {
  return new CompassusCarePlanApiError(
    response?.detail?.message || fallback,
    response?.detail?.code
  );
}

export type ICompassusCarePlanRepository = ReturnType<
  typeof CompassusCarePlanRepositoryFactory
>;

export const CompassusCarePlanRepositoryFactory = ({
  APIProvider,
  AuthService,
  APIRoutes,
}: {
  APIProvider: IAPIProvider;
  AuthService: IAuthService;
  APIRoutes: IAPIRoutes;
}) => {
  const { GET, PATCH, POST } = APIProvider;
  const DELETE = APIProvider.DELETE;
  const { getAuthenticationToken } = AuthService;

  const route = (template: string, clinicId: number, patientId: number) =>
    template
      .replace("{clinic_id}", clinicId.toString())
      .replace("{patient_id}", patientId.toString());

  const cleanParams = (params?: Record<string, unknown>) => {
    if (!params) return {};
    return Object.fromEntries(
      Object.entries(params).filter(
        ([, value]) => value !== undefined && value !== null && value !== ""
      )
    );
  };

  const appendQueryParams = (
    baseUrl: string,
    params?: Record<string, unknown>
  ): string => {
    const cleaned = cleanParams(params);
    const query = Object.entries(cleaned)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
      )
      .join("&");
    if (!query) return baseUrl;
    return `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}${query}`;
  };

  const getCarePlanAPI = async ({
    clinic_id,
    patient_id,
  }: {
    clinic_id: number;
    patient_id: number;
  }): Promise<{ care_plan: ICompassusCarePlan }> => {
    const url = route(APIRoutes.apiCompassusCarePlan, clinic_id, patient_id);
    const response = await GET(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );
    if (!response?.care_plan)
      throw carePlanApiError(response, "Failed to fetch care plan");
    return response;
  };

  const patchCarePlanAPI = async ({
    clinic_id,
    patient_id,
    body,
  }: {
    clinic_id: number;
    patient_id: number;
    body: IPatchCompassusCarePlanRequest;
  }): Promise<{ care_plan: ICompassusCarePlan }> => {
    const url = route(APIRoutes.apiCompassusCarePlan, clinic_id, patient_id);
    const response = await PATCH(url, body, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
    if (!response?.care_plan)
      throw carePlanApiError(response, "Failed to save care plan");
    return response;
  };

  const acquireEditLockAPI = async ({
    clinic_id,
    patient_id,
    editing_session_id,
  }: {
    clinic_id: number;
    patient_id: number;
    editing_session_id: string;
  }): Promise<{ care_plan: ICompassusCarePlan; acquired: boolean }> => {
    const url = `${route(
      APIRoutes.apiCompassusCarePlan,
      clinic_id,
      patient_id
    )}/edit-lock`;
    const response = await POST(
      url,
      { editing_session_id },
      { Authorization: `Bearer ${await getAuthenticationToken()}` }
    );
    if (!response?.care_plan)
      throw new Error(
        response?.detail?.message || "Failed to acquire edit lock"
      );
    return response;
  };

  const releaseEditLockAPI = async ({
    clinic_id,
    patient_id,
    editing_session_id,
  }: {
    clinic_id: number;
    patient_id: number;
    editing_session_id: string;
  }): Promise<{ care_plan: ICompassusCarePlan; acquired: boolean }> => {
    const url = `${route(
      APIRoutes.apiCompassusCarePlan,
      clinic_id,
      patient_id
    )}/edit-lock`;
    const response = await DELETE(
      url,
      { editing_session_id },
      { Authorization: `Bearer ${await getAuthenticationToken()}` }
    );
    if (!response?.care_plan)
      throw new Error(
        response?.detail?.message || "Failed to release edit lock"
      );
    return response;
  };

  const getVersionsAPI = async ({
    clinic_id,
    patient_id,
    params,
  }: {
    clinic_id: number;
    patient_id: number;
    params?: {
      month_offset?: number;
      days_back?: number;
      days_forward?: number;
      exclude_days_back?: number;
    };
  }): Promise<{ versions: ICompassusCarePlanVersion[] }> => {
    const url = appendQueryParams(
      route(APIRoutes.apiCompassusCarePlanVersions, clinic_id, patient_id),
      params
    );
    const response = await GET(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );
    if (!response?.versions)
      throw new Error(
        response?.detail?.message || "Failed to fetch care plan versions"
      );
    return response;
  };

  const getVersionAPI = async ({
    clinic_id,
    patient_id,
    version_id,
  }: {
    clinic_id: number;
    patient_id: number;
    version_id: number;
  }): Promise<{ version: ICompassusCarePlanVersion }> => {
    const url = route(
      APIRoutes.apiCompassusCarePlanVersion,
      clinic_id,
      patient_id
    ).replace("{version_id}", version_id.toString());
    const response = await GET(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );
    if (!response?.version)
      throw new Error(
        response?.detail?.message || "Failed to fetch care plan version"
      );
    return response;
  };

  const patchVersionAPI = async ({
    clinic_id,
    patient_id,
    version_id,
    body,
  }: {
    clinic_id: number;
    patient_id: number;
    version_id: number;
    body: { version_name: string | null };
  }): Promise<{ version: ICompassusCarePlanVersion }> => {
    const url = route(
      APIRoutes.apiCompassusCarePlanVersion,
      clinic_id,
      patient_id
    ).replace("{version_id}", version_id.toString());
    const response = await PATCH(url, body, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
    if (!response?.version)
      throw new Error(
        response?.detail?.message || "Failed to update care plan version"
      );
    return response;
  };

  const getVersionDiffAPI = async ({
    clinic_id,
    patient_id,
    version_id,
  }: {
    clinic_id: number;
    patient_id: number;
    version_id: number;
  }): Promise<{
    version: ICompassusCarePlanVersion;
    previous_version: ICompassusCarePlanVersion | null;
    lines: ICompassusCarePlanDiffLine[];
  }> => {
    const url = route(
      APIRoutes.apiCompassusCarePlanVersionDiff,
      clinic_id,
      patient_id
    ).replace("{version_id}", version_id.toString());
    const response = await GET(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );
    if (!response?.version || !response?.lines)
      throw new Error(
        response?.detail?.message || "Failed to fetch version diff"
      );
    return response;
  };

  const restoreVersionAPI = async ({
    clinic_id,
    patient_id,
    version_id,
  }: {
    clinic_id: number;
    patient_id: number;
    version_id: number;
  }): Promise<{ care_plan: ICompassusCarePlan }> => {
    const url = route(
      APIRoutes.apiCompassusCarePlanVersionRestore,
      clinic_id,
      patient_id
    ).replace("{version_id}", version_id.toString());
    const response = await POST(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );
    if (!response?.care_plan)
      throw new Error(
        response?.detail?.message || "Failed to restore care plan version"
      );
    return response;
  };

  const exportCarePlanAPI = async ({
    clinic_id,
    patient_id,
  }: {
    clinic_id: number;
    patient_id: number;
  }): Promise<string> => {
    const url = route(
      APIRoutes.apiCompassusCarePlanExport,
      clinic_id,
      patient_id
    );
    const response = await GET(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );
    if (typeof response === "string") return response;
    throw carePlanApiError(response, "Failed to export care plan");
  };

  return {
    getCarePlanAPI,
    patchCarePlanAPI,
    acquireEditLockAPI,
    releaseEditLockAPI,
    getVersionsAPI,
    getVersionAPI,
    patchVersionAPI,
    getVersionDiffAPI,
    restoreVersionAPI,
    exportCarePlanAPI,
  };
};
